import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  UserCheck,
  UserX,
  Eye,
  Crown
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { groupService } from '../services/groupService';
import { Button, LoadingSpinner, UserProfilePopover, ProfileAvatar } from '../components/ui';
import { PageHeader, ContentCard } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import type { Group, GroupMember } from '../types';
// User profile modal functionality

const GroupManagePage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [group, setGroup] = useState<Group | null>(null);
  const [pendingRequests, setPendingRequests] = useState<GroupMember[]>([]);
  const [currentMembers, setCurrentMembers] = useState<GroupMember[]>([]);
  const [declinedMembers, setDeclinedMembers] = useState<GroupMember[]>([]);
  const [activeTab, setActiveTab] = useState<'pending' | 'members' | 'declined'>('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAction, setIsLoadingAction] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const numericGroupId = groupId ? parseInt(groupId) : null;

  useEffect(() => {
    if (numericGroupId) {
      loadGroupData();
    }
  }, [numericGroupId]);

  const loadGroupData = async () => {
    if (!numericGroupId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load group details
      const groupData = await groupService.getGroupById(numericGroupId);
      setGroup(groupData);

      // Check if user is the owner
      if (groupData.ownerId !== user?.id) {
        toast.error('Fout', 'Je bent niet geautoriseerd om deze groep te beheren');
        navigate(`/groups/${groupId}`);
        return;
      }

      // Load all members data in parallel
      const [requestsData, membersData] = await Promise.all([
        groupService.getGroupRequests(numericGroupId),
        groupService.getGroupMembers(numericGroupId)
      ]);

      setPendingRequests(requestsData);

      // Filter members by status
      const { members } = membersData;
      setCurrentMembers(members.filter(m => m.status === 'ACCEPTED'));
      setDeclinedMembers(members.filter(m => m.status === 'REJECTED'));


    } catch (error: any) {
      console.error('Failed to load group data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load group data';
      setError(errorMessage);
      if (error.response?.status === 404) {
        toast.error('Fout', 'Groep niet gevonden');
        navigate('/groups');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberAction = async (memberId: number, action: 'accept' | 'reject') => {
    if (!numericGroupId) return;

    try {
      setIsLoadingAction(memberId);

      // Backend will automatically send notification to the member

      if (action === 'accept') {
        await groupService.acceptMember(numericGroupId, memberId);
        toast.success('Succes', 'Lid succesvol geaccepteerd');

        // ✅ Notifications are now sent via backend API and delivered in real-time via Socket.IO
      } else {
        await groupService.rejectMember(numericGroupId, memberId);
        toast.success('Succes', 'Lid succesvol afgewezen');

        // ✅ Notifications are now sent via backend API and delivered in real-time via Socket.IO
      }

      // Reload data to reflect changes
      await loadGroupData();
    } catch (error: any) {
      console.error(`Failed to ${action} member:`, error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${action} member`;
      toast.error('Fout', errorMessage);
    } finally {
      setIsLoadingAction(null);
    }
  };

  const handleReconsiderMember = async (memberId: number) => {
    if (!numericGroupId) return;

    try {
      setIsLoadingAction(memberId);

      // In a real implementation, you'd want a separate endpoint for this
      // For now, we'll simulate by accepting the member
      await groupService.acceptMember(numericGroupId, memberId);
      toast.success('Succes', 'Lid heroverwogen en geaccepteerd');

      await loadGroupData();
    } catch (error: any) {
      console.error('Failed to reconsider member:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reconsider member';
      toast.error('Fout', errorMessage);
    } finally {
      setIsLoadingAction(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderMemberCard = (member: GroupMember, actions: React.ReactNode) => (
    <div key={member.userId} className="sf-item p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <UserProfilePopover
            userId={member.userId}
            placement="auto"
            trigger={
              <div className="flex-shrink-0 cursor-pointer">
                <ProfileAvatar
                  user={member.user}
                  size="md"
                  className="hover:ring-2 hover:ring-neon-cyan transition-all"
                />
              </div>
            }
          />
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-[15px] font-semibold text-fg-bright">
                {member.user.username}
              </h3>
              {member.userId === group?.ownerId && (
                <Crown className="h-4 w-4 text-neon-lime" />
              )}
            </div>
            <div className="text-sm text-fg-muted">
              <div>Verzoek: {formatDate(member.joinedAt)}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <UserProfilePopover
            userId={member.userId}
            placement="auto"
            trigger={
              <button
                className="p-2 text-fg-faint transition-colors hover:text-neon-cyan"
                title="Profiel bekijken"
              >
                <Eye className="h-4 w-4" />
              </button>
            }
          />
          {actions}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Groepsbeheer laden..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-field border border-neon-pink/40 bg-neon-pink/10 p-4">
          <div className="text-sm text-neon-pink-soft">{error}</div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <h2 className="text-lg font-bold text-fg-bright mb-2">Groep niet gevonden</h2>
        <p className="text-fg-muted">De groep die je zoekt bestaat niet of is verwijderd.</p>
        <Link
          to="/groups"
          className="sf-btn-primary px-4 py-2.5 text-sm mt-4"
        >
          Terug naar groepen
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Groep beheren"
        subtitle={group.name}
        breadcrumb={
          <>
            <button
              onClick={() => navigate(`/groups/${groupId}`)}
              className="flex items-center gap-2 text-sm text-fg-muted transition-colors hover:text-fg-soft"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar groep
            </button>
            <div className="sf-eyebrow mt-4">// groepen</div>
          </>
        }
      />

      <ContentCard>
        {/* Group Summary */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-fg-bright mb-4">Overzicht</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="sf-item p-4 text-center">
              <div className="text-2xl font-bold text-neon-cyan">
                {pendingRequests.length}
              </div>
              <div className="sf-label-mono mt-1">Wachtende verzoeken</div>
            </div>
            <div className="sf-item p-4 text-center">
              <div className="text-2xl font-bold text-neon-lime">
                {currentMembers.length}
              </div>
              <div className="sf-label-mono mt-1">Huidige leden</div>
            </div>
            <div className="sf-item p-4 text-center">
              <div className="text-2xl font-bold text-neon-pink-soft">
                {declinedMembers.length}
              </div>
              <div className="sf-label-mono mt-1">Afgewezen gebruikers</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/[.08] mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-neon-cyan text-fg-bright'
                  : 'border-transparent text-fg-muted hover:text-fg-soft transition-colors duration-200'
              }`}
            >
              Wachtende verzoeken
              {pendingRequests.length > 0 && (
                <span className="ml-2 rounded-full bg-neon-cyan/[.12] px-2 py-0.5 font-mono text-xs font-semibold text-neon-cyan">
                  {pendingRequests.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'members'
                  ? 'border-neon-cyan text-fg-bright'
                  : 'border-transparent text-fg-muted hover:text-fg-soft transition-colors duration-200'
              }`}
            >
              Huidige leden ({currentMembers.length})
            </button>
            <button
              onClick={() => setActiveTab('declined')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'declined'
                  ? 'border-neon-cyan text-fg-bright'
                  : 'border-transparent text-fg-muted hover:text-fg-soft transition-colors duration-200'
              }`}
            >
              Afgewezen gebruikers ({declinedMembers.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
        {activeTab === 'pending' && (
          <>
            {pendingRequests.length === 0 ? (
              <div className="text-center py-12">
                <UserCheck className="mx-auto h-12 w-12 text-fg-faint mb-4" />
                <h3 className="text-lg font-bold text-fg-bright mb-2">Geen wachtende verzoeken</h3>
                <p className="text-fg-muted italic">Alle verzoeken zijn beoordeeld.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((member) =>
                  renderMemberCard(
                    member,
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handleMemberAction(member.userId, 'accept')}
                        loading={isLoadingAction === member.userId}
                        size="sm"
                        variant="primary"
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        Accepteren
                      </Button>
                      <Button
                        onClick={() => handleMemberAction(member.userId, 'reject')}
                        loading={isLoadingAction === member.userId}
                        size="sm"
                        variant="danger"
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        Afwijzen
                      </Button>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'members' && (
          <>
            {currentMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-fg-faint mb-4" />
                <h3 className="text-lg font-bold text-fg-bright mb-2">Nog geen leden</h3>
                <p className="text-fg-muted italic">Er zijn nog geen leden geaccepteerd.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {currentMembers.map((member) =>
                  renderMemberCard(
                    member,
                    member.userId !== group.ownerId ? (
                      <Button
                        onClick={() => handleMemberAction(member.userId, 'reject')}
                        loading={isLoadingAction === member.userId}
                        size="sm"
                        variant="danger"
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        Verwijderen
                      </Button>
                    ) : (
                      <span className="sf-badge-lime">
                        Eigenaar
                      </span>
                    )
                  )
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'declined' && (
          <>
            {declinedMembers.length === 0 ? (
              <div className="text-center py-12">
                <UserX className="mx-auto h-12 w-12 text-fg-faint mb-4" />
                <h3 className="text-lg font-bold text-fg-bright mb-2">Geen afgewezen gebruikers</h3>
                <p className="text-fg-muted italic">Er zijn nog geen gebruikers afgewezen.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {declinedMembers.map((member) =>
                  renderMemberCard(
                    member,
                    <Button
                      onClick={() => handleReconsiderMember(member.userId)}
                      loading={isLoadingAction === member.userId}
                      size="sm"
                      variant="secondary"
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Heroverweging
                    </Button>
                  )
                )}
              </div>
            )}
          </>
        )}
        </div>
      </ContentCard>
    </div>
  );
};

export default GroupManagePage;